import React, { useState, useEffect } from 'react';
import {
    Card, Tabs, Table, Button, Row, Col, Statistic, message, Spin, Empty, InputNumber, Divider, Typography, Space
} from 'antd';
import { EditOutlined, SaveOutlined, CalculatorOutlined } from '@ant-design/icons';
import axios from 'axios';
import UniversalModuleModal from './UniversalModuleModal';
import { MODULE_MODAL_CONFIGS } from './moduleModalConfigs';

const { Title, Text } = Typography;

const AggregatePlanning = ({ fileId, fileName, setSelectedFile, initialConfig }) => {
    const [activeTab, setActiveTab] = useState('input');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);

    // Config & Input State
    const [periods, setPeriods] = useState(6);
    const [allowBackorders, setAllowBackorders] = useState(false);
    const [periodData, setPeriodData] = useState([]);
    const [costs, setCosts] = useState({
        cost_regular: 0,
        cost_overtime: 0,
        cost_subcontract: 0,
        initial_inventory: 0,
        cost_inventory: 0,
        idle_rt_cost: 0,
        backorder_cost: 0
    });

    // Results State
    const [results, setResults] = useState(null);

    const getAuthToken = () => localStorage.getItem('access_token');

    // Initialize from file or modal
    useEffect(() => {
        if (fileId) {
            loadFileData(fileId);
        } else if (initialConfig) {
            setupInitialConfig(initialConfig);
        }
    }, [fileId, initialConfig]);

    const setupInitialConfig = (config) => {
        const n = config.periods || 6;
        setPeriods(n);
        setAllowBackorders(config.allow_backorders || false);

        // Initialize empty period data if none provided
        const initialPeriods = Array(n).fill().map((_, i) => ({
            key: i,
            period: i + 1,
            demand: 0,
            capacity_regular: 0,
            capacity_overtime: 0,
            capacity_subcontract: 0
        }));
        setPeriodData(initialPeriods);
    };

    const loadFileData = async (id) => {
        try {
            const token = getAuthToken();
            if (!token) return;

            setIsLoading(true);
            const response = await axios.get(`/api/get_AggregatePlanning/${id}/`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const { input_data, output_data } = response.data;

            if (input_data) {
                const n = input_data.periods;
                setPeriods(n);
                setAllowBackorders(input_data.allow_backorders);

                // Reconstruction of period data table
                const reconstructed = Array(n).fill().map((_, i) => ({
                    key: i,
                    period: i + 1,
                    demand: input_data.demand?.[i] || 0,
                    capacity_regular: input_data.capacity_regular?.[i] || 0,
                    capacity_overtime: input_data.capacity_overtime?.[i] || 0,
                    capacity_subcontract: input_data.capacity_subcontract?.[i] || 0
                }));
                setPeriodData(reconstructed);

                setCosts({
                    cost_regular: input_data.cost_regular ?? input_data.costs?.regular_time ?? 0,
                    cost_overtime: input_data.cost_overtime ?? input_data.costs?.overtime ?? 0,
                    cost_subcontract: input_data.cost_subcontract ?? input_data.costs?.subcontracting ?? 0,
                    initial_inventory: input_data.initial_inventory ?? 0,
                    cost_inventory: input_data.cost_inventory ?? input_data.holding_cost ?? 0,
                    idle_rt_cost: input_data.idle_rt_cost ?? 0,
                    backorder_cost: input_data.backorder_cost ?? 0
                });
            }

            if (output_data) {
                setResults(output_data);
                setActiveTab('output');
            }

            setIsSaved(true);
            setIsLoading(false);
        } catch (error) {
            console.error('Error loading file:', error);
            message.error('Failed to load analysis data');
            setIsLoading(false);
        }
    };

    const handleInputChange = (key, field, value) => {
        const newData = [...periodData];
        const index = newData.findIndex(item => item.key === key);
        if (index > -1) {
            newData[index][field] = value;
            setPeriodData(newData);
        }
    };

    const handleCostChange = (field, value) => {
        setCosts(prev => ({ ...prev, [field]: value }));
    };

    const saveData = async (modalConfig = null) => {
        try {
            const token = getAuthToken();
            if (!token) {
                message.error("User is not authenticated.");
                return;
            }

            setIsLoading(true);

            // If periods changed via modal, we need to adjust the local table
            let currentPeriods = modalConfig ? modalConfig.periods : periods;
            let currentAllowBackorders = modalConfig ? modalConfig.allow_backorders : allowBackorders;
            let currentPeriodData = periodData;

            if (modalConfig && modalConfig.periods !== periods) {
                // Resize data array
                const newN = modalConfig.periods;
                const resized = Array(newN).fill().map((_, i) => {
                    if (i < periodData.length) return periodData[i];
                    return {
                        key: i,
                        period: i + 1,
                        demand: 0,
                        capacity_regular: 0,
                        capacity_overtime: 0,
                        capacity_subcontract: 0
                    };
                });
                currentPeriodData = resized;
                setPeriodData(resized);
                setPeriods(newN);
                setAllowBackorders(currentAllowBackorders);
            }

            const payload = {
                name: modalConfig ? modalConfig.name : (fileName || 'Aggregate Plan'),
                input_data: {
                    periods: currentPeriods,
                    demand: currentPeriodData.map(d => d.demand),
                    capacity_regular: currentPeriodData.map(d => d.capacity_regular),
                    capacity_overtime: currentPeriodData.map(d => d.capacity_overtime),
                    capacity_subcontract: currentPeriodData.map(d => d.capacity_subcontract),
                    cost_regular: costs.cost_regular,
                    cost_overtime: costs.cost_overtime,
                    cost_subcontract: costs.cost_subcontract,
                    cost_inventory: costs.cost_inventory,
                    initial_inventory: costs.initial_inventory,
                    idle_rt_cost: costs.idle_rt_cost,
                    allow_backorders: currentAllowBackorders,
                    backorder_cost: currentAllowBackorders ? costs.backorder_cost : 0
                }
            };

            const headers = { Authorization: `Bearer ${token}` };
            let response;

            if (fileId) {
                response = await axios.put(`/api/update_AggregatePlanning/${fileId}/`, payload, { headers });
            } else {
                response = await axios.post('/api/save_AggregatePlanning/', payload, { headers });
            }

            setResults(response.data.output_data);
            setActiveTab('output');
            setIsSaved(true);
            message.success('Analysis saved successfully');
            setIsLoading(false);

        } catch (error) {
            console.error('Error saving data:', error);
            const errorMsg = error.response?.data?.error || 'Failed to save analysis';
            message.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
            setIsLoading(false);
        }
    };

    const inputColumns = [
        { title: 'Period', dataIndex: 'period', key: 'period', width: 80, fixed: 'left' },
        {
            title: 'Demand',
            dataIndex: 'demand',
            key: 'demand',
            render: (val, record) => (
                <InputNumber min={0} value={val} onChange={(v) => handleInputChange(record.key, 'demand', v)} style={{ width: '100%' }} />
            )
        },
        {
            title: 'Regular Capacity',
            dataIndex: 'capacity_regular',
            key: 'capacity_regular',
            render: (val, record) => (
                <InputNumber min={0} value={val} onChange={(v) => handleInputChange(record.key, 'capacity_regular', v)} style={{ width: '100%' }} />
            )
        },
        {
            title: 'Overtime Capacity',
            dataIndex: 'capacity_overtime',
            key: 'capacity_overtime',
            render: (val, record) => (
                <InputNumber min={0} value={val} onChange={(v) => handleInputChange(record.key, 'capacity_overtime', v)} style={{ width: '100%' }} />
            )
        },
        {
            title: 'Subcontract Capacity',
            dataIndex: 'capacity_subcontract',
            key: 'capacity_subcontract',
            render: (val, record) => (
                <InputNumber min={0} value={val} onChange={(v) => handleInputChange(record.key, 'capacity_subcontract', v)} style={{ width: '100%' }} />
            )
        },
    ];

    const renderInputTab = () => (
        <div>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card title="Costs & Parameters" size="small">
                        <Row gutter={[16, 8]}>
                            <Col xs={12} sm={8} md={6}>
                                <Text strong>Regular Time Cost ($/unit):</Text>
                                <InputNumber min={0} value={costs.cost_regular} onChange={(v) => handleCostChange('cost_regular', v)} style={{ width: '100%' }} />
                            </Col>
                            <Col xs={12} sm={8} md={6}>
                                <Text strong>Overtime Cost ($/unit):</Text>
                                <InputNumber min={0} value={costs.cost_overtime} onChange={(v) => handleCostChange('cost_overtime', v)} style={{ width: '100%' }} />
                            </Col>
                            <Col xs={12} sm={8} md={6}>
                                <Text strong>Subcontracting Cost ($/unit):</Text>
                                <InputNumber min={0} value={costs.cost_subcontract} onChange={(v) => handleCostChange('cost_subcontract', v)} style={{ width: '100%' }} />
                            </Col>
                            <Col xs={12} sm={8} md={6}>
                                <Text strong>Holding Cost ($/unit-period):</Text>
                                <InputNumber min={0} value={costs.cost_inventory} onChange={(v) => handleCostChange('cost_inventory', v)} style={{ width: '100%' }} />
                            </Col>
                            <Col xs={12} sm={8} md={6}>
                                <Text strong>Initial Inventory (units):</Text>
                                <InputNumber min={0} value={costs.initial_inventory} onChange={(v) => handleCostChange('initial_inventory', v)} style={{ width: '100%' }} />
                            </Col>
                            <Col xs={12} sm={8} md={6}>
                                <Text strong>Idle RT Cost ($/unit):</Text>
                                <InputNumber min={0} value={costs.idle_rt_cost} onChange={(v) => handleCostChange('idle_rt_cost', v)} style={{ width: '100%' }} />
                            </Col>
                            {allowBackorders && (
                                <Col xs={12} sm={8} md={6}>
                                    <Text strong>Backorder Cost ($/unit-period):</Text>
                                    <InputNumber min={0} value={costs.backorder_cost} onChange={(v) => handleCostChange('backorder_cost', v)} style={{ width: '100%' }} />
                                </Col>
                            )}
                        </Row>
                    </Card>
                </Col>

                <Col span={24}>
                    <Card
                        title="Period Data (Demand & Capacities)"
                        size="small"
                        extra={
                            <Button size="small" icon={<EditOutlined />} onClick={() => setIsEditModalVisible(true)}>
                                Setup Periods
                            </Button>
                        }
                    >
                        <Table
                            dataSource={periodData}
                            columns={inputColumns}
                            pagination={false}
                            bordered
                            size="small"
                            scroll={{ x: 600, y: 400 }}
                        />
                    </Card>
                </Col>

                <Col span={24} style={{ textAlign: 'right' }}>
                    <Button
                        type="primary"
                        icon={<CalculatorOutlined />}
                        onClick={() => saveData()}
                        loading={isLoading}
                        size="large"
                    >
                        Solve & Save
                    </Button>
                </Col>
            </Row>

            <UniversalModuleModal
                isVisible={isEditModalVisible}
                onClose={() => setIsEditModalVisible(false)}
                onConfirm={(config) => {
                    setIsEditModalVisible(false);
                    if (config.periods !== undefined) setPeriods(config.periods);
                    if (config.allow_backorders !== undefined) setAllowBackorders(config.allow_backorders);
                }}
                config={MODULE_MODAL_CONFIGS.aggregate_planning}
                initialValues={{
                    periods: periods,
                    allow_backorders: allowBackorders
                }}
            />
        </div>
    );

    const renderOutputTab = () => {
        if (!results) {
            return <Empty description="No results yet. Solve the model to see results." />;
        }

        const { tables, total_cost: raw_total_cost } = results;

        if (!tables) {
            return (
                <Card title="Total Optimal Cost">
                    <Statistic value={raw_total_cost} precision={2} prefix="$" />
                    <Divider />
                    <Text type="secondary">Detailed tables are missing from the response.</Text>
                </Card>
            );
        }

        // Try to find the actual total cost from the summary table (which includes idle costs)
        let finalTotalCost = raw_total_cost;
        if (tables.summary && tables.summary.length > 0) {
            const totalCostRow = tables.summary.find(row =>
                (row.Method && row.Method.toLowerCase().includes('total cost')) ||
                (row.method && row.method.toLowerCase().includes('total cost')) ||
                (row.index && row.index.toLowerCase().includes('total cost'))
            );
            if (totalCostRow && (totalCostRow.Cost !== undefined || totalCostRow.cost !== undefined)) {
                finalTotalCost = totalCostRow.Cost !== undefined ? totalCostRow.Cost : totalCostRow.cost;
            }
        }

        const renderDynamicTable = (dataSource, title, excludeKeys = []) => {
            if (!dataSource || dataSource.length === 0) return null;

            // Extract columns dynamically from the keys of the first object
            const firstRow = dataSource[0];
            const keys = Object.keys(firstRow).filter(k =>
                k !== 'index' &&
                k !== 'key' &&
                !excludeKeys.includes(k)
            );

            const columns = keys.map(key => ({
                title: key.replace(/_/g, ' ').toUpperCase(),
                dataIndex: key,
                key: key,
                render: (val) => {
                    if (val === 'n/a' || val === 9999) return <Text type="secondary">n/a</Text>;
                    if (val === '' || val === null || val === undefined) return '-';
                    if (typeof val === 'number') {
                        return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    }
                    return val;
                }
            }));

            // If there's an 'index' column, add it to the left
            if (firstRow.index !== undefined) {
                columns.unshift({
                    title: '',
                    dataIndex: 'index',
                    key: 'index',
                    width: 200,
                    fixed: 'left',
                    render: (text) => <Text strong>{text}</Text>
                });
            }

            return (
                <Card title={title} size="small" style={{ marginBottom: 24 }} bordered={false} className="result-card">
                    <Table
                        dataSource={dataSource.map((d, i) => ({ ...d, key: i }))}
                        columns={columns}
                        pagination={false}
                        bordered
                        size="small"
                        scroll={{ x: 'max-content' }}
                    />
                </Card>
            );
        };

        return (
            <div style={{ padding: '8px 0' }}>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Card bordered={false} style={{ background: '#f6ffed', marginBottom: 16 }}>
                            <Statistic
                                title={<Text strong style={{ fontSize: '16px' }}>Total Optimal Plan Cost</Text>}
                                value={finalTotalCost}
                                precision={2}
                                prefix="$"
                                valueStyle={{ color: '#52c41a', fontSize: '32px', fontWeight: 'bold' }}
                            />
                            <Text type="secondary">Optimal solution found using transportation method.</Text>
                        </Card>
                    </Col>

                    <Col span={24}>
                        {renderDynamicTable(tables.summary, "Summary Table")}
                        {renderDynamicTable(tables.cost, "Aggregate Planning Cost Matrix (Unit Costs)")}
                        {renderDynamicTable(tables.transportation, "Transportation Tableau (Shipments)")}
                        {renderDynamicTable(tables.input, "Input Parameters Summary")}
                    </Col>
                </Row>
            </div>
        );
    };

    return (
        <Card
            title={fileName || "Aggregate Planning"}
            className="analysis-card"
        >
            <Spin spinning={isLoading}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: "input",
                            label: "Planning Input",
                            children: renderInputTab()
                        },
                        {
                            key: "output",
                            label: "Optimal Plan (Results)",
                            children: renderOutputTab()
                        }
                    ]}
                />
            </Spin>
        </Card>
    );
};

export default AggregatePlanning;
